import { FC, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import { ActivityCenter } from '@lendica/activity-center';
import { PayLater } from '@lendica/paylater';
import { DataConnection } from '@lendica/data-connection';
import { Wallet } from '@lendica/wallet';
import { SidebarContainer } from './SidebarContainer';
import { NotFound } from './views/NotFound';
import { env } from './api/axiosInstance';
import { DrawDown } from '@lendica/drawdown2';
import { Account } from '@lendica/account';
import { Approvals } from '@lendica/approvals';
import { FundNow } from '@lendica/fundnow';
import { Reports } from '@lendica/reports';
import { FileUpload } from '@lendica/file-upload';
import { Members } from '@lendica/membership';
import { OfferTerms } from '@lendica/offerterms';
import { ErrorBoundary, LayoutPlaceholder } from '@lendica/components';
import CompanyProviderLayout from './CompanyProviderLayout';
import { CompanyBootstrap } from './CompanyBootstrap';
import { RedirectLegacy } from './RedirectLegacy';
import { AddCompany } from './views/AddCompany';

const HandleInvalidRoute = () => {
    useEffect(() => {
        throw new Error('Portal landed on an invalid route.');
    }, []);
    return <Navigate to="/not-found" />;
};

export const AppContainer: FC = () => {
    const navigate = useNavigate();
    return (
        <ErrorBoundary fallback={<NotFound />}>
            <Routes>
                {/* Bootstrap root to first membership's company-scoped default */}
                <Route path="/" element={<CompanyBootstrap defaultSubpath="wallet" />} />
                {/* <Route
                    path="/no-companies"
                    element={
                        <Box sx={{ p: 4 }}>
                            <Typography variant="h5" gutterBottom>
                                No companies found
                            </Typography>
                            <Typography color="text.secondary">
                                Your account does not have any company memberships yet. Please
                                contact support or your administrator to get access.
                            </Typography>
                        </Box>
                    }
                /> */}

                {/* Company-scoped area wraps the shell so Sidebar sees context */}
                <Route path="c/:companyKey" element={<CompanyProviderLayout />}>
                    <Route element={<SidebarContainer />}>
                        <Route index element={<Navigate to="wallet" replace />} />
                        <Route
                            path="account/*"
                            element={
                                <Account
                                    env={env}
                                    placeholder={
                                        <LayoutPlaceholder
                                            title="Hi there!"
                                            subtitle="Your invoice or deal details will appear here."
                                        />
                                    }
                                />
                            }
                        />
                        <Route
                            path="drawdown/*"
                            element={
                                <DrawDown
                                    placeholder={
                                        <LayoutPlaceholder
                                            title="Hi there!"
                                            subtitle="Your invoice or deal details will appear here."
                                        />
                                    }
                                />
                            }
                        />
                        <Route path="paylater/*" element={<PayLater />} />
                        <Route path="fundnow/*" element={<FundNow />} />
                        <Route path="offerterms/*" element={<OfferTerms />} />
                        <Route
                            path="wallet/*"
                            element={
                                <Wallet
                                    placeholder={
                                        <LayoutPlaceholder
                                            title="Hi there!"
                                            subtitle="Your invoice or deal details will appear here."
                                        />
                                    }
                                />
                            }
                        />
                        <Route
                            path="my-approvals/*"
                            element={<Approvals onError={() => navigate('/')} />}
                        />
                        <Route path="reports/*" element={<Reports />} />
                        <Route path="file-upload/*" element={<FileUpload env={env} />} />
                        <Route path="activity-center/*" element={<ActivityCenter />} />
                        <Route path="integrations/*" element={<DataConnection env={env} />} />
                        <Route path="members/*" element={<Members />} />
                        <Route path="not-found" element={<NotFound />} />
                        {/* <Route path="no-approval" element={<NoApproval />} /> */}
                        <Route path="add-company" element={<AddCompany />} />
                        <Route path="*" element={<HandleInvalidRoute />} />
                    </Route>
                </Route>

                {/* Legacy redirects from old top-level routes to company-scoped paths */}
                <Route path="/wallet/*" element={<RedirectLegacy to="wallet" />} />
                <Route path="/paylater/*" element={<RedirectLegacy to="paylater" />} />
                <Route path="/drawdown/*" element={<RedirectLegacy to="drawdown" />} />
                <Route path="/fundnow/*" element={<RedirectLegacy to="fundnow" />} />
                <Route path="/account/*" element={<RedirectLegacy to="account" />} />
                <Route path="/my-approvals/*" element={<RedirectLegacy to="my-approvals" />} />
                <Route path="/reports/*" element={<RedirectLegacy to="reports" />} />
                <Route path="/file-upload/*" element={<RedirectLegacy to="file-upload" />} />
                <Route
                    path="/activity-center/*"
                    element={<RedirectLegacy to="activity-center" />}
                />
                <Route path="/integrations/*" element={<RedirectLegacy to="integrations" />} />
                <Route path="/members/*" element={<RedirectLegacy to="members" />} />
            </Routes>
        </ErrorBoundary>
    );
};
